import { FunctionComponent } from 'react';
import { BodyText, Panel, Title, Flex } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';

import {
  AllowanceType,
  UserResponse,
  UserPoliciesResponseSingleDevicePolicyEnum,
  OrganizationResponse,
} from '@cdaas/api-clients/dist/asset-admin-openapi';

export interface UserDetailProps {
  user: UserResponse | null;
  organization: OrganizationResponse | null;
}

const UserOrOrganizationDetail: FunctionComponent<UserDetailProps> = ({ user, organization }) => {
  const { t } = useTranslation('orderFlow');

  const renderSingleDevicePolicyValue = (singleDevicePolicy: UserPoliciesResponseSingleDevicePolicyEnum | undefined) => {
    switch (singleDevicePolicy) {
      case UserPoliciesResponseSingleDevicePolicyEnum.Enabled:
        return t('selectProduct.userDetail.singleDevicePolicy.enabled');
      case UserPoliciesResponseSingleDevicePolicyEnum.InEffect:
        return t('selectProduct.userDetail.singleDevicePolicy.inEffect');
      case UserPoliciesResponseSingleDevicePolicyEnum.Disabled:
        return t('selectProduct.userDetail.singleDevicePolicy.disabled');
      default:
        return t('selectProduct.userDetail.singleDevicePolicy.undefined');
    }
  };

  const renderAllowance = () => {
    if (user?.allowance_type === AllowanceType.Monthly || user?.allowance_type === AllowanceType.OneTime) {
      if (user?.allowance) {
        return t(
          user?.allowance_type === AllowanceType.Monthly
            ? 'selectProduct.userDetail.allowance.monthly'
            : 'selectProduct.userDetail.allowance.oneTime',
          {
            amount: user.allowance,
            currency: organization?.locale.currency.toUpperCase() || '',
          },
        );
      } else {
        return t('selectProduct.userDetail.allowance.missing');
      }
    } else if (user?.allowance_type === AllowanceType.Unlimited) {
      return t('selectProduct.userDetail.allowance.unlimited');
    } else {
      return t('selectProduct.userDetail.allowance.undefined');
    }
  };

  if (user)
    return (
      <Panel backgroundColor="white" borderRadius boxShadow="sm" padding={{ size: 'md' }} style={{ maxWidth: '608px' }}>
        <Title size="xs">{user.name || ''}</Title>
        <Flex margin={{ top: 'md' }} spaceBetween>
          <BodyText size="md" color="gray400">
            {t('selectProduct.userDetail.contactTitle')}
          </BodyText>
          <BodyText size="md" color="gray800">{`${user.phone_number}, ${user.email}`}</BodyText>
        </Flex>
        <Flex margin={{ top: 'xs' }} spaceBetween>
          <BodyText size="md" color="gray400">
            {t('selectProduct.userDetail.deviceLimitTitle')}
          </BodyText>
          <BodyText size="md" color="gray800">
            {renderSingleDevicePolicyValue(user.policies.single_device_policy)}
          </BodyText>
        </Flex>
        <Flex margin={{ top: 'xs' }} spaceBetween>
          <BodyText size="md" color="gray400">
            {t('selectProduct.userDetail.allowanceTitle')}
          </BodyText>
          <BodyText size="md" color="gray800">
            {renderAllowance()}
          </BodyText>
        </Flex>
      </Panel>
    );

  return (
    <Panel backgroundColor="white" borderRadius boxShadow="sm" padding={{ size: 'md' }} style={{ maxWidth: '608px' }}>
      <Title size="xs">Order device for organization:</Title>
      <BodyText margin={{ top: 'md' }} size="md" color="gray700">
        {organization?.name}
      </BodyText>
    </Panel>
  );
};

export default UserOrOrganizationDetail;
